<script setup lang="ts">
import ContactButtons from '@/Components/Molecules/ContactButtons.vue';
import ServiceFeatureCard from '@/Components/Molecules/ServiceFeatureCard.vue';
import ContactForm from '@/Components/Organisms/ContactForm.vue';
import ExperiencesSection from '@/Components/Organisms/ExperiencesSection.vue';
import FAQSection from '@/Components/Organisms/FAQSection.vue';
import Footer from '@/Components/Organisms/Footer.vue';
import HeroSectionWithImages from '@/Components/Organisms/HeroSectionWithImages.vue';
import Navbar from '@/Components/Organisms/Navbar.vue';
import QuickPriceCard from '@/Components/Organisms/QuickPriceCard.vue';
import Reservation from '@/Components/Organisms/Reservation.vue';
import { useTranslations } from '@/composables/useTranslations';
import { Head } from '@inertiajs/vue3';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const { t } = useTranslations();

const HOME_STATE_KEY = 'home.formState';
const isBrowser = typeof window !== 'undefined';
const STATE_EXPIRY_HOURS = 0.1; // Durée de vie du state en heures
const siteUrl = import.meta.env.VITE_SITE_URL || 'https://votre-site.com';

const metaTitle = computed(() => t('home.meta.title'));
const metaDescription = computed(() => t('home.meta.description'));
const metaOgTitle = computed(() => t('home.meta.ogTitle'));
const metaTwitterTitle = computed(() => t('home.meta.twitterTitle'));

const activeForm = ref<'quickPrice' | 'reservation'>('quickPrice');
const reservationPrefill = ref<Record<string, unknown>>({});
const servicesCarousel = ref<HTMLElement | null>(null);
let servicesInterval: ReturnType<typeof setInterval> | null = null;

function handleQuickPriceProceed(payload: Record<string, unknown>) {
    if (isBrowser) window.localStorage.removeItem('reservation.form');
    reservationPrefill.value = payload;
    activeForm.value = 'reservation';
}

function backToQuickPrice() {
    activeForm.value = 'quickPrice';
}

function hydrateHomeState() {
    if (!isBrowser) return;
    try {
        const raw = window.localStorage.getItem(HOME_STATE_KEY);
        if (!raw) return;
        const data = JSON.parse(raw);

        // Vérifier l'expiration
        if (data.timestamp) {
            const now = Date.now();
            const expiryTime = STATE_EXPIRY_HOURS * 60 * 60 * 1000;
            if (now - data.timestamp > expiryTime) {
                // State expiré, le supprimer
                window.localStorage.removeItem(HOME_STATE_KEY);
                return;
            }
        }

        if (data.activeForm) activeForm.value = data.activeForm;
        if (data.reservationPrefill) reservationPrefill.value = data.reservationPrefill;
    } catch (e) {
        console.error('Failed to load home state', e);
    }
}

function persistHomeState() {
    if (!isBrowser) return;
    const payload = {
        activeForm: activeForm.value,
        reservationPrefill: reservationPrefill.value,
        timestamp: Date.now(),
    };
    window.localStorage.setItem(HOME_STATE_KEY, JSON.stringify(payload));
}

onMounted(() => {
    hydrateHomeState();

    servicesInterval = setInterval(() => {
        const carousel = servicesCarousel.value;
        if (!carousel) return;

        const firstCard = carousel.querySelector<HTMLElement>('[data-service-card]');
        if (!firstCard) return;

        const gap = 24;
        const step = firstCard.clientWidth + gap;
        const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;
        const isLastSlide = carousel.scrollLeft + step >= maxScrollLeft - 2;

        carousel.scrollTo({
            left: isLastSlide ? 0 : carousel.scrollLeft + step,
            behavior: 'smooth',
        });
    }, 3000); // Faire défiler toutes les 4.5 secondes
});

onBeforeUnmount(() => {
    if (servicesInterval) clearInterval(servicesInterval);
});

watch([activeForm, reservationPrefill], persistHomeState, { deep: true });

let serviceImages: { title: string; description: string; image: string; imageAlt: string }[] = [];

try {
    const modules = import.meta.glob('/public/images/service_*.{jpg,jpeg,png,webp}', {
        eager: true,
        import: 'default',
    });

    serviceImages = Object.entries(modules)
        .filter(([path]) => path.includes('service_'))
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([path, module]) => {
            const fileName = path.split('/').pop() || '';
            const keyBase = fileName.match(/service_(.+)\.[^/.]+$/)?.[1] || '';
            return {
                title: t(`services.${keyBase}.title`),
                description: t(`services.${keyBase}.description`),
                image: module as string,
                imageAlt: t(`services.${keyBase}.imageAlt`),
            };
        });
} catch (e) {
    console.error('Failed to load service images', e);
}
</script>

<template>
    <Head :title="metaTitle">
        <meta name="description" :content="metaDescription" />
        <meta property="og:title" :content="metaOgTitle" />
        <meta property="og:description" :content="metaDescription" />
        <meta property="og:image" :content="`${siteUrl}/images/hero_01.webp`" />
        <meta property="og:url" :content="`${siteUrl}/reservation`" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" :content="metaTwitterTitle" />
        <meta name="twitter:description" :content="metaDescription" />
        <meta name="twitter:image" :content="`${siteUrl}/images/hero_01.webp`" />
        <!-- <script type="application/ld+json" v-html="serviceSchemaJsonLd"></script> -->
    </Head>

    <div class="flex min-h-screen flex-col bg-primary-lighter">
        <header>
            <Navbar />
        </header>

        <main class="flex-grow bg-primary-lighter pt-20">
            <h1 class="sr-only">{{ t('home.srTitle') }}</h1>

            <HeroSectionWithImages />

            <section class="bg-primary-lighter py-16">
                <div class="mx-auto max-w-7xl space-y-6 px-4 sm:px-6 lg:px-8">
                    <QuickPriceCard v-if="activeForm === 'quickPrice'" @proceed="handleQuickPriceProceed" />
                    <Reservation v-else :prefill="reservationPrefill" @backToQuickPrice="backToQuickPrice" />
                </div>
            </section>

            <section id="services" class="bg-primary-lighter py-16">
                <div class="mx-auto w-full max-w-7xl space-y-6 rounded-3xl bg-primary-light p-8 px-4 shadow-lg sm:px-6 lg:px-8">
                    <h2 class="mb-12 text-center text-3xl font-bold text-dark md:text-4xl">{{ t('home.servicesSectionTitle') }}</h2>
                    <div ref="servicesCarousel" class="overflow-x-auto">
                        <div class="flex snap-x snap-mandatory gap-6 pb-2">
                            <div
                                v-for="service in serviceImages"
                                :key="service.title"
                                data-service-card
                                class="min-w-[85%] snap-start sm:min-w-[48%] lg:min-w-[31%]"
                            >
                                <ServiceFeatureCard
                                    :title="service.title"
                                    :description="service.description"
                                    :image="service.image"
                                    :image-alt="service.imageAlt"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <ExperiencesSection />

            <section id="faq">
                <FAQSection />
            </section>

            <section id="contact">
                <ContactForm />
            </section>
        </main>

        <footer>
            <Footer />
        </footer>
        <ContactButtons />
    </div>
</template>
